/**
 * Maps Add Task Order UI form state to Partner Central Task Order POST/PUT bodies.
 * Labels stay human-readable in the UI; wire values follow ui-integration-guide.md.
 */

const OPTION_PERIODS = new Set([
  "Base Year",
  "Option Year 1",
  "Option Year 2",
  "Option Year 3",
  "Option Year 4",
]);

const parseNumber = (value) => {
  const cleaned = String(value || "").replace(/[$,%/]/g, "").trim();
  if (!cleaned) return null;
  const amount = Number(cleaned);
  return Number.isFinite(amount) ? amount : NaN;
};

const toMoney = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return undefined;
  return amount.toFixed(2);
};

const toQty = (value) => {
  const amount = parseNumber(value);
  if (amount == null || Number.isNaN(amount)) return undefined;
  return amount.toFixed(2);
};

const setIf = (body, key, value) => {
  if (value === undefined || value === null || value === "") return;
  body[key] = value;
};

const distinctAccountIds = (values) => [
  ...new Set(
    (values || [])
      .map((id) => String(id || "").trim())
      .filter((id) => /^\d{12}$/.test(id))
  ),
];

const clinRows = (rows) => {
  if (!Array.isArray(rows) || !rows.length) return [];
  return rows
    .filter(
      (row) =>
        row.clinNumber?.trim() ||
        row.slinIdentifier?.trim() ||
        row.description?.trim() ||
        row.quantity?.trim() ||
        row.unitPrice?.trim() ||
        row.fundedAmount?.trim() ||
        row.accountingCode?.trim()
    )
    .map((row) => {
      const item = {
        clin_number: String(row.clinNumber || "").trim(),
        description: String(row.description || "").trim(),
        funded_amount: toMoney(row.fundedAmount) ?? "0.00",
      };
      setIf(item, "slin_identifier", String(row.slinIdentifier || "").trim());
      setIf(item, "quantity", toQty(row.quantity));
      setIf(item, "unit_price", toMoney(row.unitPrice));
      setIf(item, "accounting_code", String(row.accountingCode || "").trim());
      return item;
    });
};

/**
 * @param {object} form Add Task Order form state
 * @param {"draft"|"saved"} status UI save mode
 * @param {{ id: number|string, contract_start_date?: string, contract_end_date?: string, multi_year_commitment_amount?: string|number }} customer
 */
export const buildTaskOrderPayload = (form, status, customer) => {
  const body = {
    status: status === "draft" ? "DRAFT" : "ACTIVE",
    // Real customer organization.id (not commercial profile id).
    associated_organization_id: Number(customer.organization_id ?? customer.id),
  };

  setIf(body, "task_order_number", String(form.taskOrderNumber || "").trim());
  setIf(body, "reference_id", String(form.referenceId || "").trim());
  setIf(body, "order_date", form.orderDate || undefined);
  setIf(body, "modification_number", String(form.modificationNumber || "").trim() || "00");
  setIf(body, "parent_contract_number", String(form.parentContractNumber || "").trim());

  // Snapshot parent performance window from the customer contract (required for ACTIVE).
  setIf(body, "parent_contract_start_date", customer.contract_start_date || undefined);
  setIf(body, "parent_contract_end_date", customer.contract_end_date || undefined);
  setIf(body, "parent_contract_commitment_amount", toMoney(customer.multi_year_commitment_amount));

  setIf(body, "sub_agency_name", String(form.subAgencyName || "").trim());
  setIf(body, "contracting_officer_name", String(form.koName || "").trim());
  setIf(body, "contracting_officer_email", String(form.koEmail || "").trim());
  setIf(body, "contracting_officer_representative_name", String(form.corName || "").trim());
  setIf(body, "contracting_officer_representative_email", String(form.corEmail || "").trim());
  setIf(body, "start_date", form.startDate || undefined);
  setIf(body, "end_date", form.endDate || undefined);

  const optionPeriod = String(form.optionPeriod || "").trim();
  if (OPTION_PERIODS.has(optionPeriod)) {
    body.option_period = optionPeriod;
  }

  setIf(body, "total_obligated_amount", toMoney(form.totalObligatedAmount));
  setIf(body, "line_of_accounting", String(form.lineOfAccounting || "").trim());
  setIf(body, "payment_office_method", String(form.paymentOfficeMethod || "").trim());

  const naics = String(form.naicsCode || "").trim();
  if (naics) body.naics_code = naics;

  body.clin_slin_schedule = clinRows(form.clinRows);

  // Flat customer accounts only — API rejects payer_payee_accounts / aws_account_numbers.
  const accounts = distinctAccountIds(form.accountNumbers);
  if (accounts.length) body.accountNumbers = accounts;

  setIf(body, "compliance_category", String(form.complianceCategory || "").trim());
  setIf(body, "place_of_performance", String(form.placeOfPerformance || "").trim());
  setIf(body, "statement_of_work_instructions", String(form.statementOfWork || "").trim());

  return body;
};

/** Client-side ACTIVE completeness aligned with the Task Order module contract. */
export const validateActiveTaskOrder = (form, customer) => {
  const next = {};
  if (!String(form.taskOrderNumber || "").trim()) {
    next.taskOrderNumber = "Enter a task order number.";
  }
  if (!form.orderDate) next.orderDate = "Select an order date.";
  if (!String(form.parentContractNumber || "").trim()) {
    next.parentContractNumber = "Enter a parent contract number.";
  }
  if (!customer?.contract_start_date || !customer?.contract_end_date) {
    next.startDate = "Customer contract dates are required before saving an active task order.";
  }
  if (customer?.multi_year_commitment_amount == null || customer?.multi_year_commitment_amount === "") {
    next.totalObligatedAmount =
      next.totalObligatedAmount ||
      "Customer multi-year commitment is required before saving an active task order.";
  }
  if (!form.startDate) next.startDate = "Select a task order start date.";
  if (!form.endDate) next.endDate = "Select a task order end date.";
  if (form.startDate && form.endDate && form.endDate < form.startDate) {
    next.endDate = "End date must be on or after the start date.";
  }
  if (
    form.startDate &&
    customer?.contract_start_date &&
    form.startDate < customer.contract_start_date
  ) {
    next.startDate = "Start date must fall within the customer contract period.";
  }
  if (form.endDate && customer?.contract_end_date && form.endDate > customer.contract_end_date) {
    next.endDate = "End date must fall within the customer contract period.";
  }
  if (form.startDate && customer?.contract_end_date && form.startDate > customer.contract_end_date) {
    next.startDate = "Start date must fall within the customer contract period.";
  }
  if (form.endDate && customer?.contract_start_date && form.endDate < customer.contract_start_date) {
    next.endDate = "End date must fall within the customer contract period.";
  }

  const obligated = parseNumber(form.totalObligatedAmount);
  if (form.totalObligatedAmount == null || String(form.totalObligatedAmount).trim() === "") {
    next.totalObligatedAmount = "Enter the total obligated amount.";
  } else if (Number.isNaN(obligated) || obligated < 0) {
    next.totalObligatedAmount = "Enter a valid amount.";
  } else {
    const ceiling = parseNumber(customer?.multi_year_commitment_amount);
    if (ceiling != null && !Number.isNaN(ceiling) && obligated > ceiling) {
      next.totalObligatedAmount = "Amount cannot exceed the customer commitment ceiling.";
    }
  }

  if (!String(form.lineOfAccounting || "").trim()) {
    next.lineOfAccounting = "Enter a line of accounting.";
  }

  const naics = String(form.naicsCode || "").trim();
  if (naics && !/^\d{6}$/.test(naics)) {
    next.naicsCode = "NAICS code must be six digits.";
  }

  const emailOk = (value) => !value || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  if (!emailOk(form.koEmail)) next.koEmail = "Enter a valid email address.";
  if (!emailOk(form.corEmail)) next.corEmail = "Enter a valid email address.";

  if (!distinctAccountIds(form.accountNumbers).length) {
    next.accountNumbers = "Select at least one AWS account for this task order.";
  }

  const clinErrors = {};
  let fundedTotal = 0;
  let hasCompleteClin = false;
  (form.clinRows || []).forEach((row) => {
    const blank =
      !row.clinNumber?.trim() &&
      !row.slinIdentifier?.trim() &&
      !row.description?.trim() &&
      !row.quantity?.trim() &&
      !row.unitPrice?.trim() &&
      !row.fundedAmount?.trim() &&
      !row.accountingCode?.trim();
    if (blank) return;
    const rowErrors = {};
    if (!row.clinNumber?.trim()) rowErrors.clinNumber = "Required";
    if (!row.description?.trim()) rowErrors.description = "Required";
    const funded = parseNumber(row.fundedAmount);
    if (row.fundedAmount == null || String(row.fundedAmount).trim() === "") {
      rowErrors.fundedAmount = "Required";
    } else if (Number.isNaN(funded) || funded < 0) {
      rowErrors.fundedAmount = "Invalid";
    } else {
      fundedTotal += funded;
    }
    const qty = parseNumber(row.quantity);
    if (row.quantity?.trim() && (Number.isNaN(qty) || qty < 0)) rowErrors.quantity = "Invalid";
    const unit = parseNumber(row.unitPrice);
    if (row.unitPrice?.trim() && (Number.isNaN(unit) || unit < 0)) rowErrors.unitPrice = "Invalid";
    if (Object.keys(rowErrors).length) clinErrors[row.id] = rowErrors;
    else hasCompleteClin = true;
  });
  if (!hasCompleteClin) {
    next.clinRows = "Add at least one complete CLIN / SLIN row.";
  } else if (Object.keys(clinErrors).length) {
    next.clinRowErrors = clinErrors;
  }
  if (
    !next.totalObligatedAmount &&
    obligated != null &&
    !Number.isNaN(obligated) &&
    fundedTotal > obligated
  ) {
    next.totalObligatedAmount = "Funded CLIN total cannot exceed the obligated amount.";
  }

  return next;
};

export const OPTION_PERIOD_OPTIONS = [
  "Base Year",
  "Option Year 1",
  "Option Year 2",
  "Option Year 3",
  "Option Year 4",
];

export const PAYMENT_OFFICE_OPTIONS = [
  "WAWF / IPP — Net 30",
  "WAWF — Net 30",
  "IPP — Net 30",
  "Other",
];

export const COMPLIANCE_CATEGORY_OPTIONS = [
  "Fair Opportunity",
  "Logical Follow-on",
  "Urgent Exception",
  "Minimum Guarantee",
];
